import { useEffect } from "react";
import { useAuthStore } from "@/context";
import { useNotificationStore } from "@/context/stores/notification.store";
import {
  webSocketService,
  NotificationMessage,
} from "@/services/websocket.service";
import useApi from "@/services/use.api";
import { useToast } from "@/composables/toast/use.toast";

const { useDeleteApi, useGetApi } = useApi();

export const useNotifications = () => {
  const { currentUser, isAuthenticated } = useAuthStore();
  const {
    loading,
    notifications,
    subscriptions,
    unreadCount,
    setLoading,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
    cleanOldNotifications,
    updateSubscriptions,
    toggleSubscription,
    getSubscription,
    getUnreadNotifications,
    getNotificationsByType,
    getNotificationCount,
    getOldestNotification,
    getLatestNotification,
  } = useNotificationStore();

  // Initialiser la connexion WebSocket et les abonnements
  useEffect(() => {
    if (isAuthenticated && currentUser?.email) {
      // Nettoyer les anciennes notifications (garder seulement 30 jours)
      cleanOldNotifications(30);

      // Configurer le callback global pour persister toutes les notifications
      webSocketService.setGlobalNotificationCallback((notification) => {
        addNotification(notification);
      });

      // Connecter au WebSocket
      webSocketService.connect();

      // Callback pour recevoir les notifications et afficher les notifications browser
      const handleNotification = (notification: NotificationMessage) => {
        // Afficher une notification browser si autorisé
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(notification.title, {
            body: notification.message,
            icon: "/favicon.ico",
            tag: notification.id, // Éviter les doublons
          });
        }
      };

      // Attendre un peu que la connexion s'établisse avant de s'abonner
      const subscribeTimer = setTimeout(() => {
        subscriptions.forEach(({ topic, enabled }) => {
          if (enabled) {
            webSocketService.subscribeToTopic(
              currentUser.email,
              topic,
              handleNotification
            );
          }
        });
      }, 2000); // Attendre 2 secondes

      // Nettoyer lors du démontage
      return () => {
        clearTimeout(subscribeTimer);
        webSocketService.disconnect();
      };
    }
  }, [
    isAuthenticated,
    currentUser?.email,
    addNotification,
    cleanOldNotifications,
  ]);

  // Demander la permission pour les notifications browser
  const requestNotificationPermission = async () => {
    if ("Notification" in window && Notification.permission === "default") {
      await Notification.requestPermission();
    }
  };

  // Mettre à jour les abonnements WebSocket
  const updateWebSocketSubscriptions = (
    newSubscriptions: typeof subscriptions
  ) => {
    updateSubscriptions(newSubscriptions);

    if (isAuthenticated && currentUser?.email) {
      webSocketService.updateSubscriptions(currentUser.email, newSubscriptions);
    }
  };

  // Basculer un abonnement spécifique
  const handleToggleSubscription = (topic: string) => {
    toggleSubscription(topic);

    if (isAuthenticated && currentUser?.email) {
      const subscription = getSubscription(topic);
      if (subscription) {
        if (subscription.enabled) {
          // S'abonner avec le callback pour les notifications browser
          const handleNotification = (notification: NotificationMessage) => {
            if (
              "Notification" in window &&
              Notification.permission === "granted"
            ) {
              new Notification(notification.title, {
                body: notification.message,
                icon: "/favicon.ico",
                tag: notification.id,
              });
            }
          };

          webSocketService.subscribeToTopic(
            currentUser.email,
            topic,
            handleNotification
          );
        } else {
          // Se désabonner
          webSocketService.unsubscribeFromTopic(topic);
        }
      }
    }
  };

  const subscribeToAllTopics = () => {
    if (isAuthenticated && currentUser?.email) {
      subscriptions.forEach(({ topic, enabled }) => {
        if (enabled) {
          webSocketService.subscribeToTopic(
            currentUser.email,
            topic,
            (notification: NotificationMessage) => {
              addNotification(notification);
            }
          );
        }
      });
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    const res = await useGetApi<NotificationMessage[]>(
      "/auth/session/notifications"
    );
    const { data } = res ?? {};
    (data || []).forEach((item: any) => {
      const notification: NotificationMessage | null =
        webSocketService.messageParser(item);
      if (notification) addNotification(notification);
    });
    setLoading(false);
    // set({ notifications });
  };

  const removeFromServer = async (notificationId: string) => {
    setLoading(true);
    const res = await useDeleteApi(
      `/auth/session/notifications/${notificationId}`
    );
    const { success, message } = res ?? {};
    if (!success) {
      useToast(message ?? "Une erreur s'est produite", {
        variant: "error",
      });
      return;
    }
    const { data } = res ?? {};
    removeNotification(notificationId);
    useToast("Notification supprimée avec succès.", {
      variant: "success",
    });
    setLoading(false);
    // set({ notifications });
  };

  return {
    // State
    loading,
    notifications,
    subscriptions,
    unreadCount,
    isConnected: webSocketService.getConnectionStatus(),

    // Actions
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
    cleanOldNotifications,
    toggleSubscription: handleToggleSubscription,
    updateSubscriptions: updateWebSocketSubscriptions,
    requestNotificationPermission,

    fetchNotifications,

    // Helpers
    getSubscription,
    getUnreadNotifications,
    getNotificationsByType,
    getNotificationCount,
    getOldestNotification,
    getLatestNotification,
    subscribeToAllTopics,

    removeFromServer,
  };
};
